import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Home as HomeIcon, Briefcase, Building2, HardHat, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/lib/site";
import EMICalculator from "@/components/site/EMICalculator";

const ICONS = { Home: HomeIcon, Briefcase, Building2, HardHat };

export default function ServicesPage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="services-page" className="container-x py-16 md:py-24">
      <div className="max-w-3xl">
        <span className="eyebrow">What We Help You Access</span>
        <h1 className="mt-4 h-display text-4xl sm:text-5xl lg:text-6xl">
          Loans tailored to your stage, structure and ambition.
        </h1>
        <p className="mt-6 text-brand-sage text-lg leading-relaxed">
          Whether you're buying your first home, scaling a manufacturing line
          or financing a commercial project — here's the spectrum we cover.
        </p>
      </div>

      {/* EMI Calculator */}
      <div id="calculator" className="mt-14 scroll-mt-28">
        <EMICalculator />
      </div>

      <div className="mt-16 space-y-20">
        {SERVICES.map((s, idx) => {
          const Icon = ICONS[s.icon] || HomeIcon;
          const reverse = idx % 2 === 1;
          return (
            <section
              id={s.id}
              key={s.id}
              data-testid={`service-section-${s.id}`}
              className="grid md:grid-cols-12 gap-10 items-center scroll-mt-28"
            >
              <div className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-forest text-brand-gold">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="mt-5 h-display text-3xl sm:text-4xl">{s.title}</h2>
                <p className="mt-4 text-brand-sage text-base leading-relaxed max-w-lg">{s.blurb}</p>

                <ul className="mt-6 space-y-3">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-brand-forest/85">
                      <CheckCircle2 className="h-5 w-5 text-brand-terracotta shrink-0" />
                      <span className="text-base">{it}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to={`/apply?type=${s.id}`} data-testid={`service-apply-${s.id}`} className="btn-primary">
                    Apply for {s.title} <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/process" className="btn-secondary">See process</Link>
                </div>
              </div>

              <div className={`md:col-span-6 ${reverse ? "md:order-1" : ""}`}>
                <div className="relative">
                  <div className={`absolute h-24 w-24 rounded-2xl bg-brand-gold/30 -z-10 ${reverse ? "-top-6 -right-6" : "-top-6 -left-6"}`} />
                  <img
                    src={s.image}
                    alt={s.title}
                    className="rounded-2xl shadow-[0_30px_80px_-20px_rgba(17,42,34,0.3)] w-full object-cover aspect-[4/3]"
                  />
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
